import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import intersection from 'lodash/intersection';
import isEmpty from 'lodash/isEmpty';
import lodashMap from 'lodash/map';
import flatten from 'lodash/flatten';

import { ALL_ACCESSIBLE_GROUP_SCOPE, PERMISSIONS } from '@lib/permissions';
import { Session } from '@services/session';
import { GroupsFilter } from '@services/groups-filter';
import { IGroup } from '@app/admin/groups/admin-group.service';

@Injectable({
  providedIn: 'root',
})
export class GlobalGroupsService {
  public get globalGroups(): IGroup[] {
    return this._globalGroups;
  }
  public set globalGroups(group: IGroup[]) {
    this._globalGroups = group;
  }

  public get groupsIds(): number[] {
    return this.globalGroups.map((group: IGroup) => group.id);
  }

  private _globalGroups: IGroup[];
  private groupsDataSource = new BehaviorSubject<IGroup[]>([]);
  private groupsData$ = this.groupsDataSource.asObservable();

  constructor(private session: Session, private groupsFilter: GroupsFilter) {
    this.groupsData$.subscribe(groups => {
      this.globalGroups = groups;
    });
  }

  public updateData(data: IGroup[]): void {
    this.groupsDataSource.next(data);
  }

  public selectedGroups(permission?: PERMISSIONS): number[] {
    const groupsFilterValue: number[] = this.groupsFilter.value || [];
    const selectedGroupIds: number[] = groupsFilterValue.concat(
      flatten(
        groupsFilterValue.map(
          groupId => this.session.getGroupChildrenMapping()[groupId] || [],
        ),
      ),
    );
    if (isEmpty(selectedGroupIds)) {
      return this.allowedGroups(this.groupsIds, permission);
    } else {
      const commonGroups = intersection(this.groupsIds, selectedGroupIds);
      return commonGroups;
    }
  }

  public allowedGroups(
    groupIds: number[],
    permission?: PERMISSIONS,
  ): number[] {
    if (!permission) {
      return lodashMap(this.session.getAllowedGroups(), 'group.id').includes(
        groupIds,
      );
    }
    const groupVisibility: string = this.session.getGroupVisibility();
    if (groupVisibility !== 'all') {
      const userGroupIds: number[] =
        this.session.getGroupIdsOfPermission(permission);
      const commonGroups: number[] =
        permission === ALL_ACCESSIBLE_GROUP_SCOPE
          ? groupIds
          : intersection(groupIds, userGroupIds);
      return commonGroups;
    } else {
      return [];
    }
  }
}
